import React, {lazy, useEffect, useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Container, Row, Col, Dropdown} from 'react-bootstrap';

const LogInPopup = lazy(() => import('../Popups/AccountPopups/Authentication/LogInPopup'));
const SignUpPopup = lazy(() => import('../Popups/AccountPopups/Authentication/SignUpPopup'));
const LoginPrompt = lazy(() => import('../Popups/AccountPopups/LoginPrompt'));
const Search = lazy(() => import('../Search'));
import Menu from './Menu';
import Settings from './Settings';

import {profileRoutes, routeNames} from '../../constants/routes';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useDiscount from '../../hooks/useDiscount';
import {signOut} from '../../redux/actions/auth';
import {grayScaleModeState} from '../../selectors/globalConfig';
import {getAuthorizationState, getUser} from '../../selectors/auth';
import {getUserBioState} from '../../selectors/account';
import {getUserOffersState} from '../../selectors/deals';
import {getHotelsState} from '../../selectors/hotelsSearch';

import logo from '../../../assets/accessiblego_logo-light.svg';
import bannerTravelQAs from '../../../assets/banner-travelQAs.png';
import './styles.less';

const Header = () => {
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false);
  const [showLoginPopup, setShowLoginPopup] = useState<boolean>(false);
  const [showJoinPopup, setShowJoinPopup] = useState<boolean>(false);
  const [discountPopupProps, setDiscountPopupProps] = useState<any>({});
  const [auth, setAuth] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [offer, setOffer] = useState<string>('');
  const [offerPartnerName, setOfferPartnerName] = useState<string>('');

  const navigate = useNavigate();
  const discountParams = useDiscount();
  const location = useLocation();
  const {screenWidth} = useWindowDimensions();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const greyMode = useSelector(grayScaleModeState);
  const userName = useSelector(getUser);
  const userBio = useSelector(getUserBioState);
  const authState = useSelector(getAuthorizationState);
  const offersState = useSelector(getUserOffersState);
  const hotels = useSelector(getHotelsState);

  useLayoutEffect(() => {
    const nameState = localStorage.getItem('Name');
    const userOffers = localStorage.getItem('UserOffers');
    const specialOffers = localStorage.getItem('specialOffers');

    if (authState.isAuth) {
      setAuth(true);

      if (nameState) {
        setName(nameState);
      }
    }

    if (userOffers) {
      const offer = JSON.parse(userOffers);
      setOffer(offer.offerType);
      setOfferPartnerName(offer.partnerName);
    }
    if (specialOffers) setOffer(specialOffers);

    checkDiscount();
  }, []);

  useEffect(() => {
    if (
      [routeNames.home, '/home', routeNames.hotels, routeNames.hotel].indexOf(location.pathname) ===
      -1
    ) {
      setShowSearch(true);
    } else {
      setShowSearch(false);
    }

    setShowLoginPopup(false);
    setShowJoinPopup(false);
  }, [location.pathname]);

  useEffect(() => {
    const featureIsActive = false;
    const searchPage = location.pathname === routeNames.hotels;
    const prompt = sessionStorage.getItem('loginPrompt');

    if (
      featureIsActive &&
      searchPage &&
      !auth &&
      hotels.data &&
      hotels.data?.status === 'success' &&
      !prompt
    ) {
      setShowLoginPrompt(true);
    } else {
      setShowLoginPrompt(false);
    }
  }, [location.pathname, hotels]);

  useLayoutEffect(() => {
    if (authState.isAuth) {
      if (
        userBio.data.data &&
        userBio.data.data.givenName &&
        userBio.data.data.givenName.length > 0
      ) {
        setName(userBio.data.data.givenName);
        localStorage.setItem('Name', userBio.data.data.givenName);
      } else if (userName.data && userName.data.given_name) {
        setName(userName.data.given_name);
        localStorage.setItem('Name', userName.data.given_name);
      }
    }
  }, [userBio, userName]);

  useEffect(() => {
    setAuth(authState.isAuth);
  }, [authState]);

  useEffect(() => {
    if (offersState.data.length > 0) {
      const offers = localStorage.getItem('UserOffers');
      const specialOffers = localStorage.getItem('specialOffers');

      if (!specialOffers || specialOffers !== 'fb') {
        if (offersState.data === 'giveback-participant' && offers) {
          setOfferPartnerName(JSON.parse(offers).partnerName);
        }
        setOffer(offersState.data);
      } else {
        setOffer(specialOffers);
      }
    } else {
      if (offer !== 'one-time-discount-referral') setOffer('');
    }
  }, [offersState]);

  const checkDiscount = () => {
    if (localStorage.getItem('redirection')) {
      if (location.pathname === '/r') {
        setDiscountPopupProps({
          setReadyToRedirect: discountParams.setReadyToRedirect,
          discountFB: discountParams.discountFB,
        });
      } else {
        setDiscountPopupProps({discountFB: discountParams.discountFB});
      }
    }
  };

  const handleLogin = (): void => {
    checkDiscount();

    setShowLoginPopup(true);
  };

  const handleJoin = (): void => {
    checkDiscount();

    setShowJoinPopup(true);
  };

  const handleLogout = (): void => {
    dispatch(signOut());
  };

  const vanilla = searchParams.get('v') == 't' ? 'vanilla' : '';

  return (
    <>
      <header className={`${vanilla} ac-header ${offer.length > 0 ? 'ac-header--offer' : ''}`}>
        <div className='ac-header__wrapper ac-header__wrapper--offer'>
          <a
            href='https://community.accessiblego.com/c/inspiration-4af684/save-the-dates-q-a-fort-lauderdale-kissimmee'
            target='ftLauderdaleaccTravelQAs'
          >
            <img
              src={bannerTravelQAs}
              alt='Accessible Travel Q&As with Fort Lauderdale and Kissimmee - Aug 12 and Aug 19'
              style={{width: '100%', maxWidth: 898, height: '100%', maxHeight: 63}}
            />
          </a>
        </div>
        {offer.length > 0 && (
          <div className='ac-header__wrapper ac-header__wrapper--offer'>
            {(offer === 'one-time-discount' ||
              (offer === 'one-time-discount-referral' && auth)) && (
              <p>
                Discount activated! You&apos;re set to receive a special introductory rate on your
                first hotel booking.
              </p>
            )}

            {auth && offer === 'giveback-participant' && (
              <p>
                accessibleGO&apos;s Giveback Program is activated! A portion of your booking will be
                donated to {offerPartnerName}.
              </p>
            )}

            {!auth && offer === 'one-time-discount-referral' && (
              <p>Your referral discount is still waiting! Book now for a great deal!</p>
            )}

            {auth && offer === 'fb' && (
              <p>
                Flash Summer Sale Discount activated! You&apos;re set to receive special
                introductory rates off your first hotel booking.
              </p>
            )}
          </div>
        )}

        <Container>
          <Row className='ac-header__wrapper'>
            <Col xs={12} lg={2}>
              <div className='ac-header__logo-wrap'>
                <Link to='/home' className={`logo-img ${greyMode === 'true' ? 'ac-greyMode' : ''}`}>
                  <img src={logo} alt='accessibleGO.com' width='175' height='42' />
                </Link>
                {screenWidth < 992 && showSearch && <Search location='header' />}
              </div>
            </Col>

            <Col xs={12} lg={10} className='ac-header__menu-wrapper'>
              <Menu auth={auth} />

              <div>
                <Settings />

                {auth ? (
                  <Dropdown>
                    <Dropdown.Toggle
                      variant='secondary'
                      className='ac-header__profile-button'
                      id='profile-button'
                    >
                      {name}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className='ac-header__profile-menu'>
                      <Dropdown.Item
                        onClick={() => navigate(`${routeNames.account}/${profileRoutes.profile}`)}
                        as='button'
                      >
                        My Account
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleLogout} as='button'>
                        Sign Out
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <>
                    <button
                      className={`ac-button ${greyMode === 'true' ? 'ac-greyMode' : ''}`}
                      onClick={handleJoin}
                    >
                      Sign Up
                    </button>
                    <button
                      className={`ac-button ${greyMode === 'true' ? 'ac-greyMode' : ''}`}
                      onClick={handleLogin}
                    >
                      Log in
                    </button>
                  </>
                )}
              </div>

              {showLoginPrompt && (
                <LoginPrompt
                  showPopup={showLoginPrompt}
                  setShowPopup={setShowLoginPrompt}
                  setShowLoginPopup={setShowLoginPopup}
                  setShowJoinPopup={setShowJoinPopup}
                />
              )}
              {showLoginPopup && (
                <LogInPopup
                  showPopup={showLoginPopup}
                  setShowPopup={setShowLoginPopup}
                  setShowJoinPopup={setShowJoinPopup}
                  {...discountPopupProps}
                />
              )}
              {showJoinPopup && (
                <SignUpPopup
                  showPopup={showJoinPopup}
                  setShowPopup={setShowJoinPopup}
                  setShowLoginPopup={setShowLoginPopup}
                  {...discountPopupProps}
                />
              )}
            </Col>
          </Row>
        </Container>
      </header>
      <div
        className={`${vanilla} ac-header-wrap ${offer.length > 0 ? 'ac-header-wrap--offer' : ''}`}
      ></div>
    </>
  );
};

export default Header;
